<script setup>
import moment from 'moment-timezone';
import { defineEmits, defineProps, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour.js';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  timezone: {
    type: String,
    default: null,
  },
  scheduledAt: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['onClose', 'onSchedule', 'update:show']);

const timeZone = ref(null);
const selectedDateTime = ref(null);

const { t } = useI18n();

const quicklySelection = computed(() => {
  const tz = timeZone.value || moment.tz.guess();
  const selectedTz = moment.tz(tz);

  return [
    {
      label: t('DRAFTS.SCHEDULE_SEND_MODAL_SELECTIONS.THIS_AFTERNOON'),
      format: 'MMM DD, hh:mm A',
      date: selectedTz
        .clone()
        .set({ hour: 13, minute: 0, second: 0, millisecond: 0 }),
    },
    {
      label: t('DRAFTS.SCHEDULE_SEND_MODAL_SELECTIONS.TOMORROW_MORNING'),
      format: 'MMM DD, hh:mm A',
      date: selectedTz
        .clone()
        .add(1, 'day')
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 }),
    },
    {
      label: t('DRAFTS.SCHEDULE_SEND_MODAL_SELECTIONS.TOMORROW_AFTERNOON'),
      format: 'MMM DD, hh:mm A',
      date: selectedTz
        .clone()
        .add(1, 'day')
        .set({ hour: 13, minute: 0, second: 0, millisecond: 0 }),
    },
    {
      label: t('DRAFTS.SCHEDULE_SEND_MODAL_SELECTIONS.NEXT_WEEK_MONDAY'),
      format: 'MMM DD, Y, hh:mm A',
      date: selectedTz
        .clone()
        .day(8)
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 }),
    },
  ];
});

// Min date time is from 5 minutes from now for restricting mistake scheduling
const minDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(
    now.setMinutes(now.getMinutes() + 5) - now.getTimezoneOffset() * 60000
  );
  return localTime.toISOString().slice(0, 16);
});

// Max date time is ONE month from now for restricting mistake scheduling
const maxDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(
    now.setMonth(now.getMonth() + 1) - now.getTimezoneOffset() * 60000
  );
  return localTime.toISOString().slice(0, 16);
});

const timeZones = computed(() => {
  return [...timeZoneOptions()];
});

const setSchedulable = computed(() => {
  return (
    Boolean(timeZone.value) &&
    Boolean(selectedDateTime.value) &&
    selectedDateTime.value >= minDateTime.value &&
    selectedDateTime.value <= maxDateTime.value
  );
});

const nowInTimeZone = computed(() => {
  return moment().tz(timeZone.value);
});

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

const onClose = () => {
  emit('onClose');
};

const onSchedule = () => {
  emit('onSchedule', {
    timeZone: { value: timeZone.value },
    scheduledAt: selectedDateTime.value,
  });
};

const onClickQuicklySelection = date => {
  selectedDateTime.value = date.format('YYYY-MM-DDTHH:mm');
  emit('onSchedule', {
    timeZone: { value: timeZone.value },
    scheduledAt: selectedDateTime.value,
  });
};

const loadDefault = () => {
  // Initialize timeZone and selectedDateTime from props
  if (props.timezone) {
    const matchedTimeZone = timeZones.value.find(
      tz => tz.value === props.timezone
    );
    if (matchedTimeZone) {
      timeZone.value = matchedTimeZone.value;
    }
  } else {
    const guessedTz = moment.tz.guess();
    const matchedTimeZone = timeZones.value.find(tz => tz.value === guessedTz);
    if (matchedTimeZone) {
      timeZone.value = matchedTimeZone.value;
    }
  }

  if (props.scheduledAt) {
    const scheduledMoment = moment(props.scheduledAt).tz(timeZone.value);
    selectedDateTime.value = scheduledMoment.format('YYYY-MM-DDTHH:mm');
  } else {
    selectedDateTime.value = null;
  }
};

watch(
  () => props.timezone,
  () => {
    loadDefault();
  },
  { immediate: true }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="!w-96">
    <woot-modal-header :header-title="$t('DRAFTS.SCHEDULE_SEND_MODAL_TITLE')" />
    <div class="modal-content flex flex-col">
      <div class="flex-1">
        <div class="flex flex-row justify-between items-center">
          <label class="modal-label" for="time-zone-select">
            {{ $t('DRAFTS.SCHEDULE_SEND_MODAL_TIMEZONE_LABEL') }}
          </label>
          <span v-if="timeZone" class="text-xs text-n-slate-11 text-end">
            {{ nowInTimeZone.format('MMM DD, hh:mm:ss A') }}
          </span>
        </div>
        <ComboBox
          id="time-zone-select"
          v-model="timeZone"
          :options="timeZones"
          :placeholder="$t('DRAFTS.SCHEDULE_SEND_MODAL_TIMEZONE_LABEL')"
        />
      </div>

      <div class="flex-1">
        <ul class="list-none flex flex-col gap-2">
          <li
            v-for="option in quicklySelection"
            :key="option.label"
            class="p-1 border border-n-weak dark:border-n-strong rounded-md hover:bg-n-alpha-2"
          >
            <button
              class="flex w-full flex-row justify-between items-center"
              :disabled="option.date < new Date() || !timeZone"
              @click="onClickQuicklySelection(option.date)"
            >
              <span>{{ option.label }}</span>
              <span class="text-xs text-n-slate-10">
                {{ option.date.format(option.format) }}
              </span>
            </button>
          </li>
        </ul>
        <p class="text-center text-n-slate-9 text-xs my-3 p-0">
          {{ t('DRAFTS.SCHEDULE_SEND_MODAL_SELECTIONS.OR') }}
        </p>
        <label class="flex w-full flex-row" for="specific-datetime">
          {{ t('DRAFTS.SCHEDULE_SEND_MODAL_SELECTIONS.PICK_DATE_TIME') }}
        </label>
        <div class="flex w-full">
          <input
            id="specific-datetime"
            v-model="selectedDateTime"
            type="datetime-local"
            class="!mb-0"
            autocomplete="off"
            required
            :disabled="!timeZone"
            :min="minDateTime"
            :max="maxDateTime"
          />
          <NextButton
            class="ml-3 px-6"
            :disabled="!setSchedulable"
            @click="onSchedule"
          >
            {{ t('DRAFTS.SCHEDULE_BUTTON') }}
          </NextButton>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style lang="scss">
.custom-style-multiselect {
  .multiselect__content-wrapper {
    @apply max-h-[230px] !important;

    .multiselect__content .multiselect__option {
      @apply py-0 px-3 min-h-9;
    }
  }
}
</style>
